import { AxiosResponse } from "axios";
import {
  DashboardDTO,
  PostReportDTO,
  ResponseDTO,
  SupportDTO,
  UserDTO,
  WalletWithdrawDTO,
  WalletWithdrawStatus,
} from "types";
import { WalletDTO } from "types/DTOs/wallet/wallet.dto";
import { UserManageDTO } from "types/DTOs/user/user_manage.dto";
import { get, patch } from "utils";

const baseUrl = `${process.env.REACT_APP_API_URL}/admin`;

export const getDashboardData = async (): Promise<
  AxiosResponse<ResponseDTO<DashboardDTO>>
> => {
  let url = `${baseUrl}/dashboard`;

  return get(url);
};

export const getUserList = async (
  page: number,
  size: number
): Promise<
  AxiosResponse<ResponseDTO<{ items: UserManageDTO[]; total: number }>>
> => {
  let url = `${baseUrl}/user-list/?page=${page}&limit=${size}`;

  return get(url);
};

export const putBlockUser = async (
  userId: string
): Promise<AxiosResponse<ResponseDTO<UserDTO>>> => {
  let url = `${baseUrl}/block-user/?userId=${userId}`;

  return patch(url);
};

export const putUnblockUser = async (
  userId: string
): Promise<AxiosResponse<ResponseDTO<UserDTO>>> => {
  let url = `${baseUrl}/unblock-user/?userId=${userId}`;

  return patch(url);
};

export const putDeleteUser = async (
  userId: string
): Promise<AxiosResponse<ResponseDTO<UserDTO>>> => {
  let url = `${baseUrl}/delete-user/?userId=${userId}`;

  return patch(url);
};

export const getFAQList = async (
  page: number,
  size: number
): Promise<
  AxiosResponse<ResponseDTO<{ items: SupportDTO[]; total: number }>>
> => {
  let url = `${baseUrl}/faq-list/?page=${page}&limit=${size}`;

  return get(url);
};

export const getReportPostList = async (
  page: number,
  size: number
): Promise<
  AxiosResponse<ResponseDTO<{ items: PostReportDTO[]; total: number }>>
> => {
  let url = `${baseUrl}/report-post-list/?page=${page}&limit=${size}`;

  return get(url);
};

export const getWithrawList = async (
  page: number,
  size: number
): Promise<
  AxiosResponse<ResponseDTO<{ items: WalletWithdrawDTO[]; total: number }>>
> => {
  let url = `${baseUrl}/withdraw-list/?page=${page}&limit=${size}`;

  return get(url);
};

// FAQ 처리 완료 api
export const putCompleteFAQ = async (
  supportId: string
): Promise<AxiosResponse<ResponseDTO<SupportDTO>>> => {
  let url = `${baseUrl}/faq-response-complete/?supportId=${supportId}`;

  return patch(url);
};

// FAQ 답변 작성 api
export const patchAnswerFAQ = async (
  supportId: string,
  answer: string
): Promise<AxiosResponse<ResponseDTO<SupportDTO>>> => {
  let url = `${baseUrl}/faq-answer/?supportId=${supportId}`;

  return patch(url, { answer });
};

// ─── Service Management ───

export const getServiceList = async (
  page: number,
  size: number
) => {
  return get(`${baseUrl}/service-list/?page=${page}&limit=${size}`);
};

export const patchDeactivateService = async (serviceId: string) => {
  return patch(`${baseUrl}/deactivate-service/?serviceId=${serviceId}`);
};

export const patchActivateService = async (serviceId: string) => {
  return patch(`${baseUrl}/activate-service/?serviceId=${serviceId}`);
};

// ─── Booking Management ───

export const getBookingList = async (
  page: number,
  size: number
) => {
  return get(`${baseUrl}/booking-list/?page=${page}&limit=${size}`);
};

export const patchCancelBooking = async (bookingId: string) => {
  return patch(`${baseUrl}/cancel-booking/?bookingId=${bookingId}`);
};

// 신고 게시물 삭제 api
export const deleteReportPost = async (
  reportId: string
): Promise<AxiosResponse<ResponseDTO<PostReportDTO>>> => {
  let url = `${baseUrl}/report-post/?reportId=${reportId}`;

  return patch(url);
};

// 출금 승인 api
export const putApproveWithdraw = async (
  withdrawId: string,
  status: WalletWithdrawStatus,
  rejectReason?: string
): Promise<AxiosResponse<ResponseDTO<WalletWithdrawDTO>>> => {
  let url = `${baseUrl}/approve-withdraw?withdrawId=${withdrawId}`;

  const body = {
    status,
    rejectReason,
  };

  return patch(url, body);
};

export const putUpdateWallet = async (
  userId: string,
  klav: number
): Promise<AxiosResponse<ResponseDTO<WalletDTO>>> => {
  let url = `${baseUrl}/put-update-wallet/?userId=${userId}&klav=${klav}`;

  return patch(url);
};

// ─── Universal Report Management ───

export const getAdminReportList = async (
  page: number,
  size: number
): Promise<AxiosResponse<ResponseDTO<{ items: any[]; total: number }>>> => {
  return get(`${baseUrl}/report-list/?page=${page}&limit=${size}`);
};

export const resolveAdminReport = async (
  reportId: string
): Promise<AxiosResponse<ResponseDTO<{ success: boolean }>>> => {
  const { post: postFn } = await import("utils");
  return postFn(`${baseUrl}/report-resolve`, { reportId });
};

